import uniqBy from "lodash/uniqBy"
import uniq from "lodash/uniq"
import sortBy from "lodash/sortBy"
import groupBy from "lodash/groupBy"
import { findVenueRecursive } from "services/emma/lib"
import { roomsHidden } from "lib/rooms"
import agendaTopics from "config/agendaTopics"
import agendaTopicsGrouped from "config/agendaTopicsGrouped"

const NO_GROUP = "no-group"

// Room groups (the headers in the room filter) come from the venues feed: each
// top-level venue carries a `room_groups` list and each room a matching
// `room_group`. Returns unique group names ordered by `ordering`, with ties
// keeping the feed's order.
function roomGroupOrderFromVenues(venues) {
    const groups = venues.flatMap(venue => venue.room_groups || [])
    return uniq(sortBy(groups, group => group.ordering ?? 0).map(group => group.name))
}

export default function agendaFilters(site) {
    return [
        {
            key: "days",
            label: "Select Date",
            type: "multi",
            generateFilters: agenda => {
                return uniqBy(
                    agenda.map(event => {
                        return {
                            value: event.day,
                            label: event.day,
                            // label: format(new Date(event.datetime_start), "EEEE, MMMM d"),
                            facet: "days", // must be same as key above
                        }
                    }),
                    event => event.value,
                ).filter(event => event.value !== "On Demand")
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        return event.day === filter && !event.params.on_demand
                    })
                })
            },
        },
        {
            key: "tracks",
            label: site.meeting.trackTerm || "Track",
            type: "multi",
            generateFilters: (agenda, tracks) => {
                // suppress tracks filters if flag is set in Craft
                if (site.meeting.hideTracks || site.meeting.hideTracksProgram) return []
                const tracksInEvents = uniq(agenda.flatMap(event => event.tracks))
                const orderedTracks = tracks.flatMap(track => {
                    // exclude the track if we did not find any events with this track
                    if (!tracksInEvents.includes(track.name)) return []
                    return {
                        value: track.name,
                        label: track.name,
                        facet: "tracks",
                    }
                })
                // append all other tracks that are not the tracks config but are in the events feed data
                // const otherTracks = tracksInEvents.filter(track => !tracks.includes(track.name)).sort()
                // const allTracks = [
                //     ...orderedTracks,
                //     ...otherTracks.map(track => ({ value: track, label: track, facet: "tracks" })),
                // ]
                return orderedTracks
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        return event.tracks.some(track => {
                            return track === filter
                        })
                    })
                })
            },
        },
        {
            key: "sessionType",
            label: site.meeting.sessionTypeTerm || "Session Type",
            type: "multi",
            generateFilters: (agenda, sessionTypes) => {
                // suppress session type filters if there is only one
                if (!sessionTypes) return []
                if (sessionTypes.length < 2) return []
                const sessionTypesInEvents = uniq(
                    agenda.map(event => {
                        return event.session_type_id
                    }),
                )
                return sessionTypes.flatMap(sessionType => {
                    // if the session type's id is not found in any events, don't show this session type filter
                    if (sessionTypesInEvents.indexOf(parseInt(sessionType.id, 10)) === -1) return []
                    return {
                        value: "session-" + sessionType.id,
                        label: sessionType.name_plural,
                        // description: !channel.channelStatus || channel.channelStatus === "default" ? "" : channel.channelStatus,
                        facet: "sessionType",
                    }
                })
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        return "session-" + event.session_type_id === filter
                    })
                })
            },
        },
        {
            key: "topics",
            label: "Topic",
            type: "multi",
            generateFilters: agenda => {
                // suppress topics filters if flag is set in Craft
                if (!site.meeting.showTopics) return []
                const topicsInEvents = uniq(agenda.flatMap(event => event.event_topics))
                if (site.meeting.groupTopics) {
                    return agendaTopicsGrouped.flatMap(group => {
                        const filteredTopics = group.topics.filter(topic => topicsInEvents.includes(topic))
                        const filteredTopicsArray = filteredTopics.map(topic => {
                            return {
                                label: topic,
                                value: topic,
                                facet: "topics",
                            }
                        })
                        if (!filteredTopicsArray.length) return []
                        filteredTopicsArray.unshift({
                            label: group.name,
                            value: "group",
                        })
                        return filteredTopicsArray
                    })
                } else {
                    return agendaTopics.flatMap(topic => {
                        if (!topicsInEvents.includes(topic)) return []
                        return {
                            label: topic,
                            value: topic,
                            facet: "topics",
                        }
                    })
                }
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        return event.event_topics.some(topic => {
                            return topic === filter
                        })
                    })
                })
            },
        },
        {
            key: "room",
            label: "Select " + (site.meeting.roomTerm || "Theater"),
            type: "multi",
            generateFilters: (agenda, conference, venues) => {
                // suppress room filters if flag is set in Craft, or if the meeting hides rooms
                if (site.meeting.hideRoomsFilter || roomsHidden(site)) return []
                // fish rooms out of events
                let allRoomsInEvents = agenda.flatMap(event => {
                    if (!event.venue?.name) return []
                    const _venue = findVenueRecursive(event.venue.id, venues)
                    let alternateVenue = ""
                    if (conference.additional_venues.length > 0) {
                        if (!event.venue.breadcrumb.includes(conference.venue.breadcrumb)) {
                            alternateVenue = event.venue.breadcrumb.split(" | ").pop()
                        }
                    }
                    return {
                        value: event.venue.name,
                        label: event.venue.name,
                        description: alternateVenue,
                        // the room's group (filter header) is assigned in Emma; `header` is the legacy field
                        group: _venue?.room_group?.name || _venue?.header || NO_GROUP,
                        sortOrder: _venue?.sort_order ?? 0,
                        facet: "room",
                    }
                })
                // reduce to unique room values
                allRoomsInEvents = uniqBy(allRoomsInEvents, item => item.value)
                // order by Emma's sort_order, then alphabetically (also the final order when not grouping)
                allRoomsInEvents = sortBy(allRoomsInEvents, ["sortOrder", "label"])

                const hasGroupedRooms = allRoomsInEvents.some(room => room.group !== NO_GROUP)

                // optional grouping by header (requires the `groupRoomsFilters` flag in Craft)
                if (site.meeting.groupRoomsFilters && hasGroupedRooms) {
                    const roomsByGroup = groupBy(allRoomsInEvents, room => room.group)
                    const groupOrder = roomGroupOrderFromVenues(venues)
                    // in case a room's group is missing from the venues feed, place it at the bottom
                    const leftoverGroups = Object.keys(roomsByGroup).filter(
                        group => group !== NO_GROUP && !groupOrder.includes(group),
                    )
                    // ungrouped rooms go at the top, without a header
                    allRoomsInEvents = [NO_GROUP, ...groupOrder, ...leftoverGroups].flatMap(groupName => {
                        const rooms = roomsByGroup[groupName]
                        if (!rooms) return []
                        if (groupName === NO_GROUP) return rooms
                        return [{ label: groupName, value: "group" }, ...rooms]
                    })
                }

                return allRoomsInEvents
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        return event.venue?.name === filter
                    })
                })
            },
        },
        {
            key: "cme",
            label: "Select Accreditation",
            type: "multi",
            generateFilters: agenda => {
                return uniqBy(
                    agenda.flatMap(event => {
                        if (!event.params?.credit?.length) return []
                        return event.params.credit.map(cred => {
                            return {
                                value: cred,
                                label: cred,
                                facet: "cme",
                            }
                        })
                    }),
                    item => item.value,
                )
            },
            filterFunction: (events, filterGroup) => {
                return events.filter(event => {
                    return filterGroup.some(filter => {
                        if (!event.params?.credit?.length) return false
                        return event.params.credit.some(cred => {
                            return cred === filter
                        })
                    })
                })
            },
        },
    ]
}
